import {
	useMeetingsIntegrationsSettings,
	useUserVideoConferencingStatus,
	useVideoConferencingProviderFlags,
} from "../../../../api/hooks/defs/meetings-integrations";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { useMeetingContext } from "../context";
import { type TranscriptBannerStateKind, deriveTranscriptBannerState } from "./transcript-banner-state";

/** Resolves the transcript banner state for the current meeting and viewer (creator-scoped). */
export const useTranscriptBannerState = (): TranscriptBannerStateKind => {
	const { meeting, userId } = useMeetingContext();

	const { value: ffMeetingsIntegrations } = useFeatureFlagQuery("ff-is-meetings-integrations-enabled", false);
	const { byProvider: providerEphemeralFlagOn, anyEnabled: anyProviderEphemeralFlagOn } =
		useVideoConferencingProviderFlags();

	// Gate on the meeting's own provider's ephemeral flag; a meeting without a VC link (the
	// discoverability state) just needs any provider available, mirroring useVideoConferencingFeatureEnabled.
	const vcFeatureOn =
		!!ffMeetingsIntegrations &&
		(meeting.videoConference
			? providerEphemeralFlagOn[meeting.videoConference.provider]
			: anyProviderEphemeralFlagOn);

	const { data: settings, isLoading: isSettingsLoading } = useMeetingsIntegrationsSettings({ enabled: vcFeatureOn });
	const companyVcEnabled = settings?.video_conferencing?.enabled === true;
	// The status endpoint is company-setting-gated (403s when VC is off), so only fire it once the
	// company has VC enabled — mirrors useVideoConferencingEligibility.
	const { data: status, isLoading: isStatusLoading } = useUserVideoConferencingStatus({
		enabled: vcFeatureOn && companyVcEnabled,
	});

	// Feature off, or the VC queries are still in flight → no chip/banner. Waiting for them to settle
	// avoids a flash of the wrong state on load (e.g. "disconnected" before connections resolve).
	if (!vcFeatureOn || isSettingsLoading || isStatusLoading) return "none";

	const scheduledEnd = new Date(meeting.endDateTime);
	const scheduledDurationMinutes = (scheduledEnd.getTime() - new Date(meeting.startDateTime).getTime()) / (60 * 1000);
	const now = new Date();

	return deriveTranscriptBannerState({
		isCreator: meeting.created.userId === userId,
		videoConference: meeting.videoConference,
		companyVcEnabled,
		connectedProviders: (status?.connections ?? []).map((connection) => connection.provider),
		meetingHasEnded: scheduledEnd.getTime() <= now.getTime(),
		scheduledEnd,
		scheduledDurationMinutes,
		createdAt: new Date(meeting.created.timeStamp),
		now,
	});
};
